/**
 * Utility functions for scrolling to highlighted phases in different UI contexts
 */

/**
 * Scroll to a specific phase in either table or mobile card view.
 * @param {Object} options - Configuration options
 * @param {number|string} options.phaseId - The phase ID to scroll to
 * @param {Array} options.phases - Array of phase objects
 * @param {Object} options.$refs - Vue component $refs object
 * @param {Function} options.nextTickFn - Vue $nextTick function
 * @param {string} options.scrollType - 'table' or 'mobile-cards'
 * @param {Object} [options.tableConfig] - Configuration for table scrolling
 * @param {number} [options.tableConfig.itemHeight=70] - Height of each table row
 * @param {number} [options.tableConfig.headerHeight=48] - Height of table header
 * @param {string} [options.tableConfig.virtualScrollRef='virtualScroll'] - Ref name for virtual scroll component
 * @param {Object} [options.mobileConfig] - Configuration for mobile card scrolling
 * @param {string} [options.mobileConfig.cardRefPrefix='phase-card-'] - Prefix for card ref names
 * @param {number} [options.offset=100] - Offset from top when scrolling (pixels)
 */
export function scrollToPhase({
    phaseId,
    phases,
    $refs,
    nextTickFn,
    scrollType,
    tableConfig = {},
    mobileConfig = {},
    offset = 100
}) {
    if (!phaseId || !phases || !$refs || !nextTickFn) {
        console.warn('Missing required parameters for scrollToPhase');
        return;
    }

    if (scrollType === 'table') {
        scrollToPhaseInTable({
            phaseId,
            phases,
            $refs,
            nextTickFn,
            offset,
            ...tableConfig
        });
    } else if (scrollType === 'mobile-cards') {
        scrollToPhaseCardMobile({
            phaseId,
            $refs,
            nextTickFn,
            offset,
            ...mobileConfig
        });
    } else {
        console.warn(`Unknown scroll type: ${scrollType}`);
    }
}

/**
 * Scroll to a specific phase in a virtual scroll table.
 * @param {Object} options - Configuration options
 */
function scrollToPhaseInTable({
    phaseId,
    phases,
    $refs,
    nextTickFn,
    offset = 100,
    itemHeight = 70,
    headerHeight = 48,
    virtualScrollRef = 'virtualScroll'
}) {
    const virtualScrollComponent = $refs[virtualScrollRef];
    if (!virtualScrollComponent || !phases) {
        console.warn('Virtual scroll component or phases not found');
        return;
    }

    const phaseIndex = phases.findIndex(phase => phase.id == phaseId);

    if (phaseIndex === -1) {
        console.warn(`Phase ID ${phaseId} not found in phases array`);
        return;
    }

    // Use nextTick to ensure DOM is updated with highlighting before scrolling
    nextTickFn(() => {
        // Add initial delay to allow for DOM rendering
        setTimeout(() => {
            try {
                // Get the table element
                const tableElement = virtualScrollComponent.$el;
                if (tableElement) {
                    // Calculate the approximate position of the row
                    const targetPosition =
                        headerHeight + phaseIndex * itemHeight;

                    // Get the current table position relative to the page
                    const tableRect = tableElement.getBoundingClientRect();
                    const scrollTop =
                        window.pageYOffset ||
                        document.documentElement.scrollTop;

                    // Calculate the absolute position to scroll to
                    const absolutePosition =
                        tableRect.top + scrollTop + targetPosition;

                    // Scroll the main page to bring the highlighted row into view
                    window.scrollTo({
                        top: absolutePosition - offset,
                        behavior: 'smooth'
                    });
                } else {
                    console.warn('Table element not available for scrolling');
                }
            } catch (error) {
                console.warn('Could not scroll to phase in table:', error);
            }
        }, 50);
    });
}

/**
 * Scroll to a specific phase card in mobile view.
 * @param {Object} options - Configuration options
 */
function scrollToPhaseCardMobile({
    phaseId,
    $refs,
    nextTickFn,
    offset = 100,
    cardRefPrefix = 'phase-card-'
}) {
    nextTickFn(() => {
        // Add initial delay to allow for DOM rendering
        setTimeout(() => {
            try {
                const refName = `${cardRefPrefix}${phaseId}`;
                const phaseCardRef = $refs[refName];

                if (phaseCardRef && phaseCardRef[0] && phaseCardRef[0].$el) {
                    const cardElement = phaseCardRef[0].$el;
                    const cardRect = cardElement.getBoundingClientRect();
                    const scrollTop =
                        window.pageYOffset ||
                        document.documentElement.scrollTop;
                    const targetPosition = cardRect.top + scrollTop;

                    // Scroll to the highlighted phase card with smooth behavior
                    window.scrollTo({
                        top: targetPosition - offset,
                        behavior: 'smooth'
                    });
                } else {
                    console.warn(
                        `Phase card with ID ${phaseId} not found in refs`
                    );
                }
            } catch (error) {
                console.warn('Error scrolling to phase card:', error);
            }
        }, 50);
    });
}
